import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import InputSubmit from '../../../components/common/form/input-submit';
import InputText from '../../../components/common/form/input-text';
import InputSelect from '../../../components/common/form/input-select';
import FormPopup from '../../../components/common/popup/form-popup';
import { Warning } from '../../../components/common/alert/banner';
import { TrashIcon } from '@heroicons/react/outline';

import {
    postQuotationProductExtraCost,
    getQuotationProductExtraCosts
} from '../../../api/quotation';
import {getPathParam} from "../../../utils/converter";
import {CONTAINER, EXTRA_COST, PRODUCT, QUOTATION} from "../../../api/endpoints";
import RemovePopup from "../../../components/common/popup/remove-popup";

export default function ManageProductExtraCosts({
    isOpen,
    setIsOpen,
    productInfo,
    quotationId,
    containerId,
    onUpdate,
     setRefreshList
}) {
    const { t } = useTranslation();
    const { handleSubmit, control, reset, register } = useForm();
    const [isLoading, setLoading] = useState(false);
    const [exception, setException] = useState(false);
    const [extraCosts, setExtraCosts] = useState([]);
    const [removeDetail, setRemoveDetail] = useState({});
    const [isOpenRemove, setOpenRemove] = useState(false);

    const fetchProductExtraCosts = useCallback(async () => {
        try {
            const res = await getQuotationProductExtraCosts({
                quotation_id: quotationId,
                container_id: containerId,
                product_id: productInfo.id
            });
            setExtraCosts(res.extra_costs || []);
        } catch (e) {
            setExtraCosts([]);
        }
    }, [quotationId, containerId, productInfo]);

    const onSubmit = async (data) => {
        setLoading(true);
        try {
            const payload = {
                ...data,
                price_type: data.price_type?.id ?? data.price_type,
            };

            await postQuotationProductExtraCost({
                quotation_id: quotationId,
                container_id: containerId,
                product_id: productInfo.id,
                data: payload,
            });

            await fetchProductExtraCosts();
            if (onUpdate) onUpdate(); // refresh parent
            reset();
            setException(false);
        } catch (e) {
            setException(e.message);
        } finally {
            setLoading(false);
        }
    };



    useEffect(() => {
        if (isOpen && productInfo?.id) {
            fetchProductExtraCosts();
        }
    }, [isOpen, productInfo, fetchProductExtraCosts]);

    return (
        <FormPopup title={t('app.quotations.manage_product_extra_costs')} isOpen={isOpen} setIsOpen={setIsOpen}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 gap-y-2">
                    <InputText
                        id="name"
                        label={t('app.quotations.extra_cost_name')}
                        input={{ ...register('name', { required: true }) }}
                    />

                    <Controller
                        name="price_type"
                        control={control}
                        defaultValue="percentage"
                        render={({ field }) => (
                            <InputSelect
                                label={t('app.quotations.extra_data.type')}
                                name={field.name}
                                value={field.value}
                                onChange={field.onChange}
                                options={[
                                    { id: 'percentage', label: '%' },
                                    { id: 'fixed', label: '€' }
                                ]}
                            />
                        )}
                    />

                    <InputText
                        id="cost"
                        label={t('app.quotations.extra_data.cost')}
                        type="number"
                        input={{ ...register('cost', { required: true }) }}
                    />



                    <div className="flex justify-end mt-4">
                        <InputSubmit isLoading={isLoading} label={t('app.quotations.add_extra_cost')} />
                    </div>
                </div>

                {exception && <Warning message={exception} />}
            </form>

            {extraCosts.length > 0 && (
                <div className="mt-6">
                    <h3 className="font-semibold text-lg">{t('app.quotations.extra_costs')}</h3>
                    <table className="w-full mt-2 border-collapse border border-gray-300 text-sm">
                        <thead>
                            <tr className="bg-gray-100 text-center">
                                <th className="border p-2">{t('app.quotations.extra_cost_name')}</th>
                                <th className="border p-2">{t('app.quotations.extra_data.cost')}</th>
                                <th className="border p-2">{t('app.quotations.extra_cost_include_price')}</th>
                                <th className="border p-2">{t('app.common.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {extraCosts.map((item) => (
                                <tr key={item.id} className="text-center">
                                    <td className="border p-2">{item.name}</td>
                                    <td className="border p-2">
                                        {item.cost} {item.price_type === 'percentage' ? '%' : '€'}
                                    </td>
                                    <td className="border p-2">{item.included ? '✓' : '✕'}</td>
                                    <td className="border p-2">
                                        <button type="button"
                                                onClick={() => {
                                                    const removeQuotationProductExtraCost = {
                                                        title: t("app.quotations.extra_data.remove_extra_title"),
                                                        message: t("app.quotations.extra_data.remove_extra_description"),
                                                        endpoint: getPathParam([
                                                            QUOTATION, quotationId,CONTAINER, containerId,PRODUCT, productInfo.id, EXTRA_COST, item.id]),
                                                    };
                                                    setRemoveDetail(removeQuotationProductExtraCost);
                                                    setOpenRemove(true);
                                                }}

                                        >
                                            <TrashIcon className="w-4 h-4 text-red-600 hover:text-red-800" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <>
                {isOpenRemove && (
                    <RemovePopup
                        isOpen={isOpenRemove}
                        setIsOpen={setOpenRemove}
                        detail={removeDetail}
                        setDetail={async (r) => {
                            await fetchProductExtraCosts()
                            await setRefreshList(true);
                        }}
                    />
                )}
            </>

        </FormPopup>
    );
}
